<template>
  <div class="banner-gpt">
    <Sidebar />
    
    <div class="gpt-container">
      <div class="header">
        <h1>GPT Banner Management</h1>
        <p class="subtitle">Manage your banners through natural language commands</p>
      </div>
      
      <div class="banner-display" v-if="banners.length > 0">
        <h2>Current Banners</h2>
        <div class="banner-grid">
          <div v-for="banner in banners" :key="banner.id" class="banner-card">
            <div class="status-badge" :class="{ 'active': banner.is_active, 'inactive': !banner.is_active }">
              {{ banner.is_active ? 'Active' : 'Inactive' }}
            </div>
            <img :src="banner.image_url" class="banner-image" :alt="banner.title">
            <div class="banner-content">
              <h3>{{ banner.title }}</h3>
              <p class="banner-order">Order: {{ banner.order }}</p>
              <p class="banner-description" v-html="truncateDescription(banner.description)"></p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="chat-interface">
        <div class="chat-messages">
          <div v-for="(message, index) in chatHistory" :key="index" 
               class="message" :class="{ 'user-message': message.type === 'user', 'assistant-message': message.type === 'assistant', 'system-message': message.type === 'system' }">
            <div class="message-icon">
              <i v-if="message.type === 'user'" class="fas fa-user"></i>
              <i v-else-if="message.type === 'assistant'" class="fas fa-robot"></i>
              <i v-else class="fas fa-info-circle"></i>
            </div>
            <div class="message-content" v-html="message.content"></div>
          </div>
        </div>
        
        <div class="user-input">
          <textarea 
            v-model="userPrompt" 
            placeholder="Type your banner management command (e.g., 'Create a new banner for the Spring Sale')"
            @keydown.enter.prevent="submitPrompt"
            :disabled="isProcessing"
            rows="2"
          ></textarea>
          <button @click="submitPrompt" :disabled="isProcessing || !userPrompt.trim()">
            <i class="fas" :class="isProcessing ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
          </button>
        </div>
        
        <div class="command-examples">
          <h3>Example Commands:</h3>
          <ul>
            <li @click="fillPrompt('Create a new banner for Summer Course Sale with title Summer Special and set it as active')">
              Create a new banner for Summer Course Sale with title "Summer Special" and set it as active
            </li>
            <li @click="fillPrompt('Update banner with ID 12 to change the title to New Year Offer and set order to 1')">
              Update banner with ID 12 to change the title to "New Year Offer" and set order to 1
            </li>
            <li @click="fillPrompt('Delete the banner with ID 5')">
              Delete the banner with ID 5
            </li>
            <li @click="fillPrompt('Make the banner with ID 3 inactive')">
              Make the banner with ID 3 inactive
            </li>
            <li @click="fillPrompt('Show me all active banners')">
              Show me all active banners
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Sidebar from '../Sidebar.vue';

export default {
  name: 'BannerGPT',
  components: { Sidebar },
  data() {
    return {
      userPrompt: '',
      chatHistory: [
        {
          type: 'system',
          content: 'Welcome to the GPT Banner Manager! I can help you create, update, delete, and manage banners through natural language commands. Type your requests below.'
        }
      ]
    };
  },
  computed: {
    ...mapState({
      isProcessing: state => state.gptProcessing,
      gptResponse: state => state.gptResponse
    }),
    ...mapGetters({
      banners: 'getBanners'
    })
  },
  async created() {
    await this.fetchBanners();
    this.addSystemMessage('I\'ve loaded your current banners. You have ' + this.banners.length + ' banners.');
  },
  methods: {
    ...mapActions([
      'fetchBanners',
      'processBannerCommand'
    ]),
    
    async submitPrompt() {
      if (!this.userPrompt.trim() || this.isProcessing) return;
      
      // Add user message to chat history
      this.addUserMessage(this.userPrompt);
      const prompt = this.userPrompt;
      this.userPrompt = '';
      
      try {
        // Process with GPT
        const response = await this.processBannerCommand(prompt);
        
        // Add GPT response to chat history
        if (response && response.message) {
          this.addAssistantMessage(response.message);
        } else {
          this.addAssistantMessage('I\'ve processed your request, but didn\'t receive a specific message to display.');
        }
        
        // Refresh banners if needed
        if (['create', 'update', 'delete', 'toggle', 'list'].includes(response?.action)) {
          await this.fetchBanners();
        }
      } catch (error) {
        this.addSystemMessage('Error: ' + (error.message || 'An unknown error occurred.'));
      }
    },
    
    addUserMessage(content) {
      this.chatHistory.push({
        type: 'user',
        content
      });
      this.scrollToBottom();
    },
    
    addAssistantMessage(content) {
      this.chatHistory.push({
        type: 'assistant',
        content
      });
      this.scrollToBottom();
    },
    
    addSystemMessage(content) {
      this.chatHistory.push({
        type: 'system',
        content
      });
      this.scrollToBottom();
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        const chatContainer = document.querySelector('.chat-messages');
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      });
    },
    
    fillPrompt(prompt) {
      this.userPrompt = prompt;
      this.$nextTick(() => {
        document.querySelector('textarea').focus();
      });
    },
    
    truncateDescription(description) {
      if (!description) return '';
      // Strip HTML
      const text = description.replace(/<[^>]*>/g, '');
      return text.length > 100 ? text.substring(0, 97) + '...' : text;
    }
  }
};
</script>

<style scoped>
.banner-gpt {
  display: flex;
  min-height: 100vh;
  background-color: var(--background-color);
}

.gpt-container {
  flex: 1;
  margin-left: 250px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.header {
  text-align: center;
  margin-bottom: 1rem;
}

.header h1 {
  font-size: 2rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.subtitle {
  color: var(--text-light);
  font-size: 1.1rem;
}

.banner-display {
  background-color: var(--surface-color);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
}

.banner-display h2 {
  margin-bottom: 1rem;
  font-size: 1.5rem;
  color: var(--text-color);
}

.banner-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.banner-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.banner-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.status-badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 5;
}

.status-badge.active {
  background-color: var(--success-color);
  color: white;
}

.status-badge.inactive {
  background-color: var(--warning-color);
  color: white;
}

.banner-image {
  width: 100%;
  height: 140px;
  object-fit: cover;
}

.banner-content {
  padding: 1rem;
}

.banner-content h3 {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  color: var(--text-color);
}

.banner-order {
  font-size: 0.875rem;
  color: var(--text-light);
  margin-bottom: 0.5rem;
}

.banner-description {
  font-size: 0.875rem;
  color: var(--text-light);
  line-height: 1.4;
}

.chat-interface {
  background-color: var(--surface-color);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: var(--shadow-md);
  height: 500px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding-right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.message {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  max-width: 85%;
}

.user-message {
  align-self: flex-end;
  background-color: var(--primary-color);
  color: white;
}

.assistant-message {
  align-self: flex-start;
  background-color: var(--secondary-200);
  color: var(--text-color);
}

.system-message {
  align-self: center;
  background-color: var(--info-lighter);
  color: var(--text-color);
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
}

.message-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-content {
  line-height: 1.5;
}

.user-input {
  display: flex;
  gap: 0.5rem;
  background-color: white;
  border-radius: 8px;
  padding: 0.5rem;
  border: 1px solid var(--border-color);
}

.user-input textarea {
  flex: 1;
  border: none;
  resize: none;
  padding: 0.5rem;
  outline: none;
  font-family: var(--font-family-base);
  font-size: 0.95rem;
}

.user-input button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: var(--primary-color);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.user-input button:hover:not(:disabled) {
  background-color: var(--primary-darker);
}

.user-input button:disabled {
  background-color: var(--primary-200);
  cursor: not-allowed;
}

.command-examples {
  margin-top: 1rem;
}

.command-examples h3 {
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.command-examples ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.command-examples li {
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background-color: var(--divider-color);
  color: var(--text-color);
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.command-examples li:hover {
  background-color: var(--border-color);
}

/* Scrollbar styling */
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background-color: var(--border-color);
  border-radius: 20px;
}

@media (max-width: 768px) {
  .gpt-container {
    margin-left: 0;
    padding: 1rem;
  }
  
  .banner-grid {
    grid-template-columns: 1fr;
  }
  
  .chat-interface {
    height: 400px;
  }
  
  .message {
    max-width: 95%;
  }
}
</style> 